/**
 * VisitorsErrorHandler — the global Angular ErrorHandler.
 *
 * Catches UNCAUGHT errors (template/effect throws + window error/rejection,
 * forwarded by provideBrowserGlobalErrorListeners).
 *
 * Contract (zoneless-safe):
 *   1. ALWAYS console.error FIRST — a telemetry/toast failure must never
 *      swallow the original error.
 *   2. Skip the user toast for UNAUTHORIZED — mid-session token expiry is
 *      owned by SessionRecoveryService (it redirects to login) so a generic
 *      "something went wrong" toast would be wrong + redundant.
 *   3. captureException to PostHog (no-op without a key).
 *   4. Show ONE replace-in-place error toast (id 'app-error'); tolerate the
 *      toast stack not being mounted yet (early-boot errors).
 */

import { ErrorHandler, Injectable, inject } from '@angular/core';
import { PaperToastService } from '@ricoh-paper/angular';

import { AnalyticsService } from './analytics.service';
import { SessionRecoveryService } from './session-recovery.service';
import { isApiError } from './gql-client';

/** Unwrap Angular's ngOriginalError wrapper to reach the real cause. */
function unwrap(error: unknown): unknown {
  if (error && typeof error === 'object' && 'ngOriginalError' in error) {
    return (error as { ngOriginalError?: unknown }).ngOriginalError ?? error;
  }
  return error;
}

@Injectable()
export class VisitorsErrorHandler implements ErrorHandler {
  private readonly analytics = inject(AnalyticsService);
  private readonly toasts = inject(PaperToastService);
  private readonly recovery = inject(SessionRecoveryService);

  handleError(error: unknown): void {
    // (1) Log first — nothing below may swallow the error.
    console.error(error);

    try {
      const err = unwrap(error);

      // (2) Auth expiry → session recovery owns the redirect, no toast.
      if (isApiError(err) && err.code === 'UNAUTHORIZED') {
        this.recovery.handleExpiry();
        return;
      }

      // (3) Telemetry (no-op without a PostHog key).
      this.analytics.captureException(err);

      // (4) One user-facing toast, replace-in-place.
      this.showToast();
    } catch (secondary) {
      console.error('[error-handler] secondary failure', secondary);
    }
  }

  private showToast(): void {
    try {
      this.toasts.show({
        id: 'app-error',
        title: $localize`:@@visitors-error-toast-title:Something went wrong`,
        body: $localize`:@@visitors-error-toast-body:An unexpected error occurred. Try refreshing the page.`,
        severity: 'error',
        priority: 'high',
      });
    } catch {
      /* toast stack not mounted yet (early boot) — already captured + logged */
    }
  }
}
